import { z } from "zod";

import type { ContractStatus } from "./contract";

/**
 * Tenant-private çalışma katmanı şemaları (CLOS-154 `[140]`).
 *
 * Kanonik sözleşme üzerinde her organizasyonun kendi not/görev verisi. Varsayılan
 * görünürlük SAHİBİ organizasyon; başka organizasyona görünmek yalnız açık paylaşım
 * komutuyla olur ve katılımcı ilişkisi sona erince kendiliğinden kapanır.
 */

export const createContractWorkItemSchema = z.strictObject({
  versionId: z.uuid("Geçerli bir versiyon seçilmelidir").optional(),
  itemType: z.enum(["note", "task"], "Geçerli bir nesne türü seçilmelidir"),
  body: z
    .string()
    .trim()
    .min(1, "İçerik zorunludur")
    .max(5000, "İçerik en fazla 5000 karakter olabilir"),
});
export type CreateContractWorkItemInput = z.infer<typeof createContractWorkItemSchema>;

export type WorkItemVisibility = "private" | "shared";

export type ContractWorkItemView = {
  id: string;
  contractId: string;
  versionId: string | null;
  itemType: "note" | "task";
  body: string;
  visibility: WorkItemVisibility;
  sharedAt: string | null;
  createdByName: string;
  createdAt: string;
};

/** Katılımcı organizasyonun ortak görünümü — KANONİK kimlikler, kopya değil. */
export type SharedContractView = {
  contractId: string;
  title: string;
  ownerTenantId: string;
  ownerName: string;
  accessLevel: "read" | "write";
  /**
   * Erişimin bittiği an; süresiz erişimde `null` (CLOS-147 `[128]`).
   *
   * ⚠ Haricî katılımcı erişiminin ne zaman biteceğini GÖRMELİ — kartın kapsamı *"süre ve
   * erişim durumu"* diyor. Görmezse bir gün sayfayı açtığında neden kapandığını hiçbir
   * yerde okuyamaz ve karşı tarafa "sistem bozuk" diye döner.
   *
   * ⚠ `null` "bitiş tarihi YOK" demek, "hemen bitiyor" değil — ekran ikisini karıştırmamalı.
   */
  accessEndsAt: string | null;
  /**
   * SÖZLEŞMENİN KANONİK DURUMU (Shared Detail §6/§8).
   *
   * ⚠ Frontend TAHMİN ETMİYOR: durum sahibin `contracts.status` kolonundan geliyor ve
   * ekran onu tek merkezî eşlemeyle kullanıcıya dönük evreye çeviriyor. Ekranın pending
   * işlerden durum çıkarması, aynı gerçeği iki yerde ayrı ayrı hesaplamak olurdu.
   */
  status: ContractStatus;
  /** Son güncelleme — "bu sözleşme ne zaman değişti?" sorusu ekranın ilk sorularından. */
  updatedAt: string;
  /**
   * HUKUKİ TARAFLAR — GÜVENLİ PROJEKSİYON (Shared Detail §7.3).
   *
   * ⚠ ÜÇ ALAN, DAHA FAZLASI DEĞİL. Karşı taraf sözleşmenin hukuki taraflarını görmeli;
   * ama hukuki taraf ile organizasyonun ÖZEL İÇ VERİSİ aynı şey değil. Üyelik listeleri,
   * kayıt erişimi grant'leri, departman, iç sorumluluk ve rol/yetki bilgisi bu DTO'ya
   * hiç girmiyor — payload'a koyup UI'da gizlemek sızıntının kendisidir.
   *
   * ⚠ Kaynak GÜNCEL KANONİK taraflar: supersede edilmiş satırlar ve legacy `workspace`
   * kayıtları dışarı çıkmıyor (Principal Remediation). Eski DB semantiği burada yeniden
   * yorumlanmıyor; ekran principal remediation'ın SONUCUNU tüketiyor.
   */
  parties: SharedContractPartyView[];
  /**
   * ⚠ YALNIZ HİZMET SAĞLAYICI YÜZEYİNDE DOLU (CLOS-1185). Sağlayıcı sözleşmeyi kendi
   * çalışma alanında görürken hangi taraf ADINA çalıştığını bilmek zorunda; taraf olarak
   * görünseydi kendi sözleşmesi sanırdı. Katılımcı organizasyon kendi adına hareket
   * ettiği için orada `null`.
   */
  servedPartyName?: string | null;
};

/** Karşı tarafa açılan hukuki taraf görünümü — üç alan, fazlası yok. */
export type SharedContractPartyView = {
  displayName: string;
  /** Tür bilinmiyorsa uydurulmuyor. */
  entityType: "person" | "organization" | null;
  /** İki grup: temsil edilen saf ve karşı saf. Konum bilinmiyorsa `null`. */
  side: "represented" | "counterparty" | null;
};

export type SharedContractDetailView = SharedContractView & {
  /** Sahibin AÇIKÇA paylaştığı çalışma nesneleri — özel katman sızmaz. */
  sharedWorkItems: ContractWorkItemView[];
  /** Nesne bazlı paylaşımların güvenli görünümleri (CLOS-250 `[143]`). */
  sharedObjects: import("./contractObjectShare").SharedObjectView[];
  /**
   * BU SÖZLEŞMEDE OKUYANIN KENDİ KAPSAMI (Mega E2E BUG-008).
   *
   * ⚠ NEDEN GEREKLİ: paylaşılan yüzey düğmelerini `can(...)` ile, yani okuyanın KENDİ
   * organizasyonundaki rolüyle çiziyordu. Sunucu ise iki eksen soruyor: rol VE katılım
   * kapsamı (`requireParticipantCapability`). İki eksen ayrışınca ekran basılabilen ama
   * 403 dönen bir düğme gösteriyordu — canlı ölçümde "Kabul et"e basan karşı taraf tam
   * olarak bunu yaşadı ve müzakere döngüsü kapanamadı.
   *
   * ⚠ SIZINTI DEĞİL: bu liste okuyanın KENDİ yetkisi. Sahibin iç yetkileri, üyelikleri ya
   * da başka katılımcıların kapsamı buraya girmiyor.
   *
   * ⚠ YETKİ KAYNAĞI DEĞİL: karar yine sunucuda veriliyor. Bu alan yalnız ekranın
   * yapılamayacak bir şeyi teklif etmemesi için var.
   */
  capabilities: string[];
};

/**
 * PAYLAŞILAN YÜZEYİN BEKLEYEN İŞLERİ (CLOS-883).
 *
 * ### ⚠ Neden AYRI görünümler — sahip özetleri olduğu gibi verilemez
 *
 * Sahip tarafındaki `ContractReconciliationSummary` akıştaki BÜTÜN görevleri, her tarafın
 * temsilci adını ve gerekçe notlarını taşıyor; `ContractSignatureRequestSummary` de bütün
 * imzacı planını. Karşı tarafa olduğu gibi verilseydi, bir organizasyon ötekinin kimi
 * görevlendirdiğini, kimin ne gerekçeyle itiraz ettiğini ve imza sırasının tamamını
 * okurdu — paylaşılan yüzeyin *"özel katman sızmaz"* kuralının ihlali.
 *
 * Bu iki görünüm YALNIZ alıcının kendi karar vereceği kalemi taşıyor: hangi sözleşmenin
 * hangi versiyonunda, ne zamana kadar, hangi komutu vereceği.
 *
 * ⚠ `versionNumber` DAHİL: kişi neyi onayladığını/imzaladığını görmeden karar veremez;
 * ham `versionId` ekranda bir UUID'den başka bir şey değil.
 */
export type SharedReconciliationTaskView = {
  taskId: string;
  reconciliationId: string;
  contractId: string;
  versionId: string;
  versionNumber: number;
  /** Akışın son tarihi; yoksa `null` — "tarih yok" demek, "geçti" değil. */
  dueDate: string | null;
  description: string | null;
};

export type SharedSignatureTaskView = {
  signerId: string;
  requestId: string;
  contractId: string;
  versionId: string;
  versionNumber: number;
  /** Sıralı imzada kişinin sırası; paralelde de yazılı ve anlamı aynı. */
  position: number;
  required: boolean;
  signingMode: "serial" | "parallel";
  /**
   * ⚠ İmzacının KENDİ satırının durumu — talebin durumu değil. Kişi "gördüm" dedikten
   * sonra hâlâ imzalayacak; ekran ikisini karıştırırsa iş bitmiş görünür.
   */
  status: import("./contractSignature").ContractSignerStatus;
  expiresAt: string | null;
  description: string | null;
};
